/**
 * MobileCTABar Component
 * 
 * Fixed bottom CTA bar for mobile devices.
 * Only appears when user scrolls past the hero section.
 * Hides when the CTA section is visible (to avoid duplicate CTAs).
 */

import { useState, useEffect, RefObject } from "react";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Plus } from "lucide-react";
import { useLanguageStore } from "@/stores/language";
import { mobileCTAContent } from "@/data";
import { cn } from "@/utils/cn";

interface MobileCTABarProps {
  variant: "browse" | "add-to-cart";
  price?: number;
  currencyCode?: string;
  onAction: () => void;
  /** Ref to the CTA section - bar hides when this section is in view */
  ctaSectionRef?: RefObject<HTMLElement>;
}

export const MobileCTABar = ({ 
  variant, 
  price, 
  currencyCode = "USD",
  onAction,
  ctaSectionRef
}: MobileCTABarProps) => {
  const { t } = useLanguageStore();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // For add-to-cart variant (product pages), always show immediately
    if (variant === "add-to-cart") {
      setIsVisible(true);
      return;
    }

    // For browse variant (homepage), show after scrolling and hide when CTA in view
    const handleScroll = () => {
      const scrollThreshold = window.innerHeight * 0.8;
      const isPastHero = window.scrollY > scrollThreshold;
      
      let ctaSectionVisible = false;
      if (ctaSectionRef?.current) {
        const rect = ctaSectionRef.current.getBoundingClientRect();
        ctaSectionVisible = rect.top < window.innerHeight && rect.bottom > 0;
      }
      
      setIsVisible(isPastHero && !ctaSectionVisible);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, [ctaSectionRef, variant]);

  const browseText = t(mobileCTAContent.browseCollections);
  const addToCartText = `${t(mobileCTAContent.addToCart)} — $${price?.toFixed(2)}`;

  // Use CSS-only responsive visibility (md:hidden) to avoid JS-based reflows
  return (
    <div 
      className={cn(
        "fixed bottom-0 left-0 right-0 z-50 md:hidden",
        "bg-background/95 backdrop-blur-md border-t border-border",
        "px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]",
        "transition-transform duration-300 ease-out",
        isVisible ? "translate-y-0" : "translate-y-full"
      )}
    >
      <Button 
        onClick={onAction}
        className="w-full text-base py-5 font-body font-semibold touch-target"
        size="lg"
      >
        {variant === "browse" ? (
          <>
            <ShoppingBag className="mr-2 h-5 w-5" />
            {browseText}
          </>
        ) : (
          <>
            <Plus className="mr-2 h-5 w-5" />
            {addToCartText}
          </>
        )}
      </Button>
    </div>
  );
};